import React from 'react';
import { motion } from 'framer-motion';
import { FiCamera, FiTrendingUp, FiCheckCircle, FiArrowRight, FiHeart, FiVideo, FiUsers, FiStar } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const InstagramMarketingPage = () => {
  useDocumentTitle('Instagram Marketing');
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-orange-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                Instagram Marketing
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Instagram Marketing helps your brand grow through visual content, real engagement, and meaningful storytelling on Instagram. It's one of the best ways to build trust, show your products, and connect with people who genuinely care about your brand.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Grow Your Instagram</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>Get Free Audit</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Instagram Marketing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Instagram Marketing?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Whether you're running a business, promoting services, or building a community, instagram marketing gives you a simple and effective way to reach your ideal audience without complicated steps or heavy advertising.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiCamera className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Content Creation</h3>
              <p className="text-gray-600 leading-relaxed">
                High-quality reels, stories, photos, and carousels designed to match your brand identity and attract the right audience.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiVideo className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Trend-Based Posts</h3>
              <p className="text-gray-600 leading-relaxed">
                We use viral trends, audio, and Instagram insights to boost your reach and help your brand stay relevant.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiStar className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Engagement</h3>
              <p className="text-gray-600 leading-relaxed">
                We handle replies, comments, and messages to build a strong and active community around your brand.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiHeart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Brand Growth Strategy</h3>
              <p className="text-gray-600 leading-relaxed">
                A smart plan that includes content ideas, posting schedules, hashtag strategies, and performance tracking for long-term, consistent growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Instagram Marketing Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive strategy for Instagram success
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Profile Optimization',
                description: 'Transform your profile with compelling bio, strategic highlights, branded aesthetics, and optimized call-to-actions.',
                icon: FiCamera
              },
              {
                step: '02',
                title: 'Visual Identity',
                description: 'Create cohesive visual brand guidelines including color palette, filters, fonts, and content themes for consistent aesthetic.',
                icon: FiStar
              },
              {
                step: '03',
                title: 'Content Calendar',
                description: 'Plan strategic mix of feed posts, Reels, Stories, and IGTV content optimized for engagement and algorithm performance.',
                icon: FiVideo
              },
              {
                step: '04',
                title: 'Content Production',
                description: 'Create high-quality visuals, engaging captions, trending Reels, and interactive Stories that capture attention.',
                icon: FiCamera
              },
              {
                step: '05',
                title: 'Engagement Strategy',
                description: 'Active community engagement including responding to comments, DMs, engaging with relevant accounts, and building relationships.',
                icon: FiHeart
              },
              {
                step: '06',
                title: 'Growth & Analytics',
                description: 'Implement growth tactics, track performance metrics, analyze insights, and optimize strategy for continuous improvement.',
                icon: FiTrendingUp
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Instagram Marketing Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grow your Instagram presence with expert management
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Basic',
                price: '₹15,000',
                duration: '/month',
                popular: false,
                features: [
                  '12 Feed Posts',
                  '15 Stories',
                  'Content Calendar',
                  'Basic Graphics',
                  'Hashtag Strategy',
                  'Monthly Reports'
                ]
              },
              {
                name: 'Professional',
                price: '₹30,000',
                duration: '/month',
                popular: true,
                features: [
                  '20 Feed Posts',
                  '30 Stories',
                  '8 Reels',
                  'Custom Graphics',
                  'Advanced Hashtags',
                  'Community Management',
                  'Competitor Analysis',
                  'Bi-Weekly Reports'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Unlimited Content',
                  'Daily Stories',
                  'Daily Reels',
                  'Influencer Campaigns',
                  'UGC Management',
                  'Instagram Ads',
                  'Weekly Reports',
                  'Dedicated Manager'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-orange-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Us for Instagram Marketing?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a trusted instagram marketing strategist and a team known for reliable social media marketing services, we focus on creating content that feels real, human, and aligned with your audience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCamera className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Relatable Content</h3>
              <p className="text-gray-600 leading-relaxed">
                We create relatable content your audience enjoys and use Instagram trends to increase your visibility naturally.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiVideo className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Consistent Brand Voice</h3>
              <p className="text-gray-600 leading-relaxed">
                Managing your brand voice through consistent posting and building high-quality engagement through comments and DMs.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Real Followers Growth</h3>
              <p className="text-gray-600 leading-relaxed">
                Growing real followers—not bots, not fake numbers. Our goal is to make your brand stand out naturally, with honest content that connects and converts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about Instagram Marketing
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How important are Reels for Instagram growth?",
                answer: "Extremely. Instagram heavily pushes Reels in the algorithm. Most accounts see 10x more reach on Reels compared to static posts. If you're not posting Reels consistently, you're leaving massive growth on the table."
              },
              {
                question: "Can you grow my account without buying followers?",
                answer: "Yes. We use organic strategies like trending Reels, hashtag research, engagement tactics and smart content to attract real followers who actually care about your brand. Fake followers hurt your account long-term."
              },
              {
                question: "What if I'm camera-shy or don't have content?",
                answer: "No problem. We can create stunning graphics, carousels, text-based Reels and B-roll videos that don't require you on camera. Many successful Instagram accounts never show a face."
              },
              {
                question: "How long before I see results?",
                answer: "Engagement improvements happen fast—usually within the first 2 weeks. Follower growth takes longer, typically 1–3 months of consistent posting before momentum builds."
              },
              {
                question: "Do I need Instagram Ads or is organic enough?",
                answer: "Depends on your goals. Organic is great for building community. Paid ads accelerate growth and drive sales faster. We recommend both—organic builds trust, paid drives immediate results."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Build an Instagram That Converts?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Let's create a feed that doesn't just look pretty—it brings in followers, engagement and paying customers.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Get Free Strategy</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              <span>Call / WhatsApp Your Team</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstagramMarketingPage;
